import React from 'react'
import Table from 'antd/lib/table'
import Button from 'antd/lib/button'
import Row from 'antd/lib/row'
import Col from 'antd/lib/col'
import Search from 'antd/lib/input/Search'

import Orders from '~/pages/Orders'
import { ConfirmButton, Spinner } from '~/components'

import { confirmVerifiedOrder, getUser } from '~/firebase.functions'
import { getOrdersRef } from '~/firebase'
import { SYMBOLS } from '~/utils'
import { OrderObject, PromiseStates } from '~/types'

import './style.css'


type UserOrderObject = OrderObject & {_key: string, _uid: string}


export default class AdminPanel extends React.Component {
  state = {
    fulltext: '',
    orders: [] as UserOrderObject[],
    userRecords: {}, // Associative object of user's details that have orders
    verificationPromiseState: 'IDLE' as PromiseStates,
    verifiedKeys: [] as string[],
  }

  ordersRef = getOrdersRef('')

  componentDidMount() {
    this.ordersRef.on('value', this.setOrders) // TODO change value to child events
  }

  componentWillUnmount() {
    if (this.ordersRef) this.ordersRef.off()
  }

  setOrders = (snapshot: firebase.database.DataSnapshot) => {
    const orders: UserOrderObject[] = []
    const userRecords: string[] = []

    snapshot.forEach(userOrders => {
      userOrders.forEach(order => {
        orders.push({...order.val(), _key: order.key, _uid: userOrders.key})
        return false
      })
      userRecords[userOrders.key!] = {}
      return false
    })

    this.setState({orders, userRecords}, this.fetchUserRecords)
  }

  fetchUserRecords() {
    for (const uid in this.state.userRecords) { // tslint:disable-line:forin
      getUser({uid})
        .then(response => this.state.userRecords[uid] = response.data) // eslint-disable-line react/no-direct-mutation-state
        .then(() => this.forceUpdate())
    }
  }

  renderWalletDate = (date: number) => new Date(date).toLocaleDateString(undefined, {weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'})

  renderFilterControls = () => (
    <Search
      placeholder="fulltext search ⟶ email, wallet address, reference symbol, variable symbol, payment method, price EUR, user ID"
      onKeyUp={this.handleFulltextKey}
      // style={{ width: 400 }}
    />
  )

  handleFulltextKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (/* Escape */e.keyCode === 27) e.currentTarget.value = ''

    this.setState({fulltext: e.currentTarget.value})
  }

  searchInOrder(o: UserOrderObject) {
    if (!this.state.fulltext.length) return true

    const user = this.state.userRecords[o._uid]
    const keywords = this.state.fulltext.toLowerCase().split(' ').filter(String)
    const searchStrings = [
      ...(user.email ? [user.email] : []),
      ...(o.cryptoPayment ? [o.cryptoPayment.address] : []),
      ...(o.bankPayment
        ? [
            o.bankPayment.referenceSymbol,
            o.bankPayment.variableSymbol,
          ]
        : []
      ),
      o.paymentMethod,
      o.priceEUR,
      o._uid,
    ].map(v => v.toLowerCase())
    const matches = keywords.filter(kw => searchStrings.some(what => what.includes(kw)))
    return keywords.length === matches.length
  }


  renderOrders = () => (
    <Table
      className="walletTable" size="small"
      dataSource={this.state.orders.filter(v => !this.checkIsVerified(v) && this.searchInOrder(v))}
      rowKey="_key"
      expandedRowRender={Orders.renderExpandedRow}
      pagination={false}
    >
      <Table.Column key="paymentMethod" dataIndex="paymentMethod" title="Payment method" width={130}/>
      <Table.Column key="priceEUR" dataIndex="priceEUR" title="Price" render={v => SYMBOLS.sformat(v, 'EUR')} width={110}/>
      <Table.Column key="user" dataIndex="_uid" title="User" width={250} render={v => this.state.userRecords[v].email || v}/>
      <Table.Column
        key="timestamp" title="Order date" dataIndex="timestamp" render={this.renderWalletDate} width={140}
      />
      <Table.Column key="actions" title="Actions" width={110} render={(row: UserOrderObject) => (
        <>
          <Button size="small" ghost type="primary" className="ant-btn-dashed_"
            onClick={this.verifyOrder} data-value={row._key}
            style={{marginRight: 5}}>Verify</Button>
          <ConfirmButton size="small" ghost type="danger" className="ant-btn-dashed"
            onClick={() => null} data-value={row._key}>Cancel</ConfirmButton>
        </>
      )}/>
    </Table>
  )

  checkIsVerified = (v: UserOrderObject) => this.state.verifiedKeys.includes(v._key)
  removeVerifiedKey = key => this.setState(({verifiedKeys}) => ({verifiedKeys: [...verifiedKeys.filter(v => v !== key)]}))
  verifyOrder = ({currentTarget: {dataset: {value: key}}}: React.SyntheticEvent<HTMLButtonElement>) => this.setState(({verifiedKeys}) => ({verifiedKeys: [...verifiedKeys, key]}))
  unVerifyOrder = ({currentTarget: {dataset: {value: key}}}: React.SyntheticEvent<HTMLButtonElement>) => this.removeVerifiedKey(key)

  renderVerifiedOrders = () => (
    <Table
      className="walletTable" size="small"
      dataSource={this.state.orders.filter(this.checkIsVerified)}
      rowKey="_key"
      expandedRowRender={Orders.renderExpandedRow}
      pagination={false}
    >
      <Table.Column key="paymentMethod" dataIndex="paymentMethod" title="Payment method" width={130}/>
      <Table.Column key="priceEUR" dataIndex="priceEUR" title="Price" render={v => SYMBOLS.sformat(v, 'EUR')} width={110}/>
      <Table.Column key="user" dataIndex="_uid" title="User" width={250} render={v => this.state.userRecords[v].email || v}/>
      <Table.Column
        key="timestamp" title="Order date" dataIndex="timestamp" render={this.renderWalletDate} width={140}
      />
      <Table.Column key="actions" title="Actions" width={110} render={(row: UserOrderObject) => (
        <>
          <Button size="small" ghost type="danger" className="ant-btn-dashed_"
            onClick={this.unVerifyOrder} data-value={row._key}>Remove</Button>
        </>
      )}/>
    </Table>
  )

  confirmVerifiedOrders = () => {
    this.setState({verificationPromiseState: 'LOADING'})
    Promise.all(
      this.state.verifiedKeys.map(key => {
        const uid = this.state.orders.find(o => o._key === key)!._uid
        return confirmVerifiedOrder({key, uid})
          .then(() => this.removeVerifiedKey(key))
          .catch(e => {
            e.key = key
            e.uid = uid
            console.dir(e) // tslint:disable-line:no-console
            throw e
          })
      })
    )
    .then(() => this.setState({verificationPromiseState: 'IDLE'})) // not 'FINISHED' for simplicity
    .catch(() => this.setState({verificationPromiseState: 'ERROR'}))
  }

  displaySpinner = () => ({
    ERROR: () => <span title="error: see details in console" style={{pointerEvents: 'initial'}}> ✗</span>,
    FINISHED: () => <span> ✓</span>,
    IDLE: () => null,
    LOADING: () => <Spinner inline/>,
  }[this.state.verificationPromiseState]())

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState.verifiedKeys.length === 0 && prevState.verificationPromiseState !== 'IDLE')
    return {verificationPromiseState: 'IDLE'}

    return {}
  }

  render() {
    return (
      <main className="isoLayoutContentWrapper sc-fjdhpX cKswoM adminPanel" style={{height: '100%'}}>

        {!!this.state.verifiedKeys.length && (
          <section className="sc-iRbamj gXBuxZ ordersWrapper">
            <h2 className="isoComponentTitle sc-jzJRlG kviPuT">Verified Orders</h2>

            <Row type="flex" justify="space-around" align="middle">
              <Col span={21}>
                <div className="isoBoxWrapper sc-chPdSV cOTLJI">
                  <div>
                    <h3 className="isoBoxTitle sc-cSHVUG ifvfLf">POZOR</h3>
                    <p className="isoBoxSubTitle sc-kAzzGY gCwUWQ">
                      Stav ověřených balíčků je nutno potvrdit a uložit do databáze tímto tlačítkem!
                      <Button size="small" type="primary" className="ant-btn-dashed_"
                        onClick={this.confirmVerifiedOrders}
                        disabled={this.state.verificationPromiseState !== 'IDLE'}
                        style={{marginLeft: 20}}>Confirm<this.displaySpinner/></Button>
                    </p>
                  </div>
                </div>
              </Col>
            </Row>

            <Row type="flex" justify="space-around" align="middle">
              <Col span={21}>
                <this.renderVerifiedOrders/>
              </Col>
            </Row>
          </section>
        )}

        <section className="sc-iRbamj gXBuxZ ordersWrapper">
          <h2 className="isoComponentTitle sc-jzJRlG kviPuT">Pending Orders</h2>
          <Row type="flex" justify="space-around" align="middle">
            <Col span={21}>
              <this.renderFilterControls/>
              <this.renderOrders/>
              <input id="jsonSwitch" type="checkbox" style={{display: 'none'}}/>
              <label htmlFor="jsonSwitch" className="jsonSwitch">
                <span className="switch"/>
                <span className="">Show JSON</span>
              </label>
              <pre className="json">
                <br/>
                {JSON.stringify(this.state.orders, null, '  ')}
              </pre>
            </Col>
          </Row>
        </section>

      </main>
    )
  }
}



// WEBPACK FOOTER //
// ./src/pages/AdminPanel/index.tsx